import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, EnvelopeSimple, ArrowLeft, House as HouseIcon } from '@phosphor-icons/react';
import { API } from '../context/AuthContext';
import { toast } from 'sonner';

/**
 * Phase 2.5a — Builder magic-link login.
 *
 * Single-screen form. We always show the same "check your inbox" state
 * after submission regardless of whether the email matches an existing
 * builder, an invited-but-unclaimed identity, or someone brand new —
 * the backend already returns 200 in all those cases for enumeration-
 * resistance, and the UI mirrors that.
 *
 * Invite deep-link support: `?email=dave@foo.com&invited_to=Plot 14`
 * pre-fills the form + shows a reassurance banner naming the property
 * they were invited to, so the first step of the builder's journey
 * is one tap instead of "what email did she use again?"
 */
export default function BuilderLogin() {
    const [params] = useSearchParams();
    const prefillEmail = params.get('email') || '';
    const invitedTo = params.get('invited_to') || '';
    const [email, setEmail] = useState(prefillEmail);
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);
    const navigate = useNavigate();

    // If the builder already has a session, skip straight to the dashboard.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await axios.get(`${API}/builder/me`, { withCredentials: true });
                if (!cancelled && r.data?.builder) navigate('/builder/dashboard', { replace: true });
            } catch { /* expected 401 — stay on login */ }
        })();
        return () => { cancelled = true; };
    }, [navigate]);

    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim() || !email.includes('@')) {
            toast.error('Enter a valid email address');
            return;
        }
        setBusy(true);
        try {
            await axios.post(
                `${API}/builder/auth/magic-link`,
                { email: email.trim() },
                { withCredentials: true, headers: { 'Content-Type': 'application/json' } },
            );
            setSent(true);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not send link. Try again.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#FAFAFA]">
            <header className="border-b border-[#18181B] bg-white px-4 sm:px-6 py-3 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2 no-underline">
                    <img src="/defectr-logo.png" alt="DefectR" className="h-7 w-auto" />
                </Link>
                <Link to="/" className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#52525B] hover:text-[#09090B] no-underline inline-flex items-center gap-1">
                    <ArrowLeft size={12} weight="bold" /> Home
                </Link>
            </header>

            <main className="max-w-md mx-auto px-4 sm:px-6 py-12 sm:py-16">
                {!sent ? (
                    <>
                        <div className="label-tiny mb-3 text-[#1FA5CF]">For builders</div>
                        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Sign in</h1>
                        <p className="mt-3 text-sm text-[#52525B] max-w-sm">
                            Enter the email address your homeowner used to invite you. We&apos;ll send a one-tap sign-in link — no password.
                        </p>

                        {invitedTo && (
                            <div className="mt-5 border border-[#1FA5CF] bg-[#E8F5FB] p-3 text-xs text-[#1787AB] flex items-start gap-2" data-testid="builder-invited-banner">
                                <HouseIcon size={16} weight="duotone" color="#1FA5CF" className="mt-0.5 flex-none" />
                                <div>
                                    You were invited to <strong>{invitedTo}</strong>.
                                    Tap <em>Send me a sign-in link</em> below — we&apos;ve pre-filled your email.
                                </div>
                            </div>
                        )}

                        <form onSubmit={submit} className="mt-8" data-testid="builder-login-form">
                            <label className="label-tiny block">Email</label>
                            <div className="relative mt-1">
                                <EnvelopeSimple size={16} weight="duotone" color="#1FA5CF" className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="dave@example-builders.co.uk"
                                    autoComplete="email"
                                    autoFocus
                                    spellCheck={false}
                                    data-testid="builder-login-email-input"
                                    className="w-full pl-9 pr-3 py-3 border border-[#E4E4E7] focus:border-[#18181B] outline-none text-sm font-mono"
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={busy}
                                data-testid="builder-login-submit"
                                className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#1FA5CF] hover:bg-[#1787AB] text-white px-4 py-3 font-mono text-xs uppercase tracking-[0.2em] disabled:opacity-50 active:scale-95 transition-all duration-150"
                            >
                                {busy ? 'Sending…' : 'Send me a sign-in link'} {!busy && <ArrowRight size={14} weight="bold" />}
                            </button>

                            <p className="mt-5 text-[10px] font-mono uppercase tracking-[0.18em] text-[#A1A1AA]">
                                Magic link expires in 15 minutes · single-use
                            </p>
                        </form>
                    </>
                ) : (
                    <div data-testid="builder-login-sent">
                        <div className="label-tiny mb-3 text-[#1FA5CF]">Almost there</div>
                        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Check your inbox</h1>
                        <p className="mt-3 text-sm text-[#52525B]">
                            If <strong>{email}</strong> is connected to a property, a sign-in link is on its way. The link works once and expires in 15 minutes.
                        </p>
                        <div className="mt-8 border border-[#E4E4E7] bg-white p-4">
                            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#52525B] mb-2">Didn&apos;t get the email?</div>
                            <ul className="text-sm text-[#52525B] space-y-1.5 list-disc pl-4">
                                <li>Check your spam / junk folder.</li>
                                <li>Make sure your homeowner has invited this exact email address.</li>
                                <li>Try again in an hour — there&apos;s a small per-email rate limit.</li>
                            </ul>
                            <button
                                onClick={() => { setSent(false); setEmail(''); }}
                                data-testid="builder-login-retry"
                                className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#1FA5CF] hover:underline"
                            >
                                Use a different email
                            </button>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}
